import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react"
import type {IBasicResponse} from "../../types/api"
import {ANALYTICS_API_URL} from "../../utils/api"
import {applyAdminRequestHeaders} from "../../utils/apiHeaders"
import type {StoreState} from "../store"
import {HEATMAP_CELL_SIZE_METERS} from "./heatmapPresentation"
import type {
    GeoAnalysisResponse,
    GeoEdgeAnalysisRequest,
    GeoHeatmapRequest,
    GeoPolygonAnalysisRequest
} from "./geoApiTypes"

const rawGeoBaseQuery = fetchBaseQuery({
    baseUrl: ANALYTICS_API_URL,
    prepareHeaders: (headers, {getState}) => {
        // Geo data is employee-scoped. The shared analytics service token must never replace this identity.
        const token = (getState() as StoreState).auth?.token
        if (token) headers.set("Authorization", `Bearer ${token}`)
        return applyAdminRequestHeaders(headers)
    }
})

const geoBaseQuery: typeof rawGeoBaseQuery = async (args, api, extraOptions) => {
    if (!ANALYTICS_API_URL) {
        return {error: {status: "CUSTOM_ERROR", error: "REACT_APP_ANALYTICS_API_URL is not configured"}}
    }

    const body = typeof args === "string" ? undefined : args.body as GeoHeatmapRequest | undefined
    // An empty permission intersection is not the same as the absence of a branch filter.
    if (body?.branch_id_list?.length === 0) {
        return {error: {status: "CUSTOM_ERROR", error: "GEO_BRANCH_SCOPE_EMPTY"}}
    }

    // Keep cancellation bound to its controller: the installed RTK Query timeout implementation passes
    // an unbound abort() listener. The server's 30-second budget gets a small transport/authentication margin.
    const controller = new AbortController()
    const onAbort = () => controller.abort()
    if (api.signal.aborted) onAbort()
    else api.signal.addEventListener("abort", onAbort, {once: true})
    let timedOut = false
    const timeoutId = setTimeout(() => {
        timedOut = true
        controller.abort()
    }, 35_000)

    try {
        const result = await rawGeoBaseQuery(args, {...api, signal: controller.signal}, extraOptions)
        return timedOut
            ? {error: {status: "TIMEOUT_ERROR", error: "Geo analysis request timed out"}, meta: result.meta}
            : result
    } finally {
        clearTimeout(timeoutId)
        api.signal.removeEventListener("abort", onAbort)
    }
}

const getGeoBody = (request: GeoHeatmapRequest): GeoHeatmapRequest => ({
    date_from: request.date_from,
    date_to: request.date_to,
    ...(request.branch_id_list !== undefined ? {branch_id_list: request.branch_id_list} : {}),
    ...(request.polygon ? {polygon: request.polygon} : {}),
    ...(request.bbox ? {bbox: request.bbox} : {}),
    cell_size_m: request.cell_size_m ?? HEATMAP_CELL_SIZE_METERS,
    ...(request.neighbor_zones?.length ? {neighbor_zones: request.neighbor_zones} : {}),
    ...(request.branch_locations?.length ? {branch_locations: request.branch_locations} : {})
})

export const geoApi = createApi({
    reducerPath: "geoApi",
    baseQuery: geoBaseQuery,
    endpoints: (build) => ({
        getGeoHeatmap: build.query<IBasicResponse<GeoAnalysisResponse>, GeoHeatmapRequest>({
            query: (request) => ({url: "v1/geo/heatmap", method: "POST", body: getGeoBody(request)})
        }),
        getGeoPolygonAnalysis: build.query<IBasicResponse<GeoAnalysisResponse>, GeoPolygonAnalysisRequest>({
            query: (request) => ({url: "v1/geo/polygon-analysis", method: "POST", body: getGeoBody(request)})
        }),
        getGeoEdgeAnalysis: build.query<IBasicResponse<GeoAnalysisResponse>, GeoEdgeAnalysisRequest>({
            query: (request) => ({
                url: "v1/geo/edge-analysis",
                method: "POST",
                body: {
                    ...getGeoBody(request),
                    branch_id: request.branch_id,
                    band_meters: request.band_meters,
                    ...(request.expansion_cells ? {expansion_cells: request.expansion_cells} : {})
                }
            })
        })
    })
})

export const {
    useGetGeoHeatmapQuery,
    useGetGeoPolygonAnalysisQuery,
    useGetGeoEdgeAnalysisQuery
} = geoApi
